import { useQuery } from '@apollo/client/react'
import { Button, Flex, Text } from '@chakra-ui/react'
import { ColumnDef } from '@tanstack/react-table'
import { useMemo } from 'react'

import { AdminPageLayout } from '../../../components/AdminPageLayout/AdminPageLayout'
import { DataTable } from '../../../components/DataTable'
import { AdminBadge } from '../../../components/StatusBadge/StatusBadge'
import {
  type GatewaySubgraphComposeStatus,
  type GatewaySubgraphStatus,
  type GatewaySupergraphStatus,
  gatewaySupergraphStatusQuery,
} from '../../../services/gatewaySupergraphService'

const statusColor: Record<GatewaySubgraphComposeStatus, string> = {
  INCLUDED: 'green',
  EXCLUDED: 'orange',
  UNREACHABLE: 'red',
}

const formatDate = (value?: string | null) => {
  if (!value) return '—'
  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString()
}

export const GatewaySupergraphAdminPage = () => {
  const {
    data,
    loading: isLoading,
    refetch,
  } = useQuery(gatewaySupergraphStatusQuery, { fetchPolicy: 'network-only' })

  const status = (data as { gatewaySupergraphStatus?: GatewaySupergraphStatus })
    ?.gatewaySupergraphStatus

  const subgraphs = status?.subgraphs ?? []

  const columns = useMemo<ColumnDef<GatewaySubgraphStatus>[]>(
    () => [
      {
        accessorKey: 'name',
        header: 'Subgraph',
      },
      {
        accessorKey: 'status',
        header: 'Status',
        cell: info => {
          const value = info.getValue() as GatewaySubgraphComposeStatus
          return (
            <AdminBadge colorScheme={statusColor[value] ?? 'gray'}>
              {value}
            </AdminBadge>
          )
        },
      },
      {
        accessorKey: 'lastComposedAt',
        header: 'Last composed',
        cell: info => formatDate(info.getValue() as string | null),
      },
      {
        accessorKey: 'url',
        header: 'URL',
        cell: info => (
          <Text fontSize="xs" color="presets.neutral.70">
            {info.getValue() as string}
          </Text>
        ),
      },
      {
        accessorKey: 'error',
        header: 'Error',
        cell: info => (
          <Text fontSize="xs" color="presets.neutral.70">
            {(info.getValue() as string | null) ?? '—'}
          </Text>
        ),
      },
    ],
    [],
  )

  return (
    <AdminPageLayout pageTitle="GraphQL Gateway">
      <Flex direction="column" gap="12px">
        <Flex align="center" gap="12px" wrap="wrap">
          <Text color="presets.neutral.70" fontSize="sm">
            Last reload: {formatDate(status?.lastReloadAt)}
          </Text>
          {status?.degraded && (
            <AdminBadge colorScheme="orange">DEGRADED</AdminBadge>
          )}
          <Button size="sm" variant="outline" onClick={() => void refetch()}>
            Refresh
          </Button>
        </Flex>
        <DataTable
          data={subgraphs}
          columns={columns}
          isLoading={isLoading}
          height="calc(100vh - 260px)"
        />
      </Flex>
    </AdminPageLayout>
  )
}
